import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// auth
import AuthAccessWrapper from 'components/auth/AuthAccessWrapper';
import { PROJECT_MODULES_SLUGS, USER_RIGHTS_KEYS } from 'utils/constants';
import { getSlugByPathname } from 'utils/helper';
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
import MainLayout from '../layouts/main';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  DynamicFormEditPage,

  //
  // Dashboard: General
  GeneralAppPage,
  HomePage,
  LoginPage,
  NewPasswordPage,
  //
  Page500,
  RegisterPage,
  ResetPasswordPage,
  StaffManagementCreatePage,
  StaffManagementEditPage,
  StaffManagementListPage,
  StaffManagementViewPage,
  QRCodeManagerCreatePage,
  QRCodeManagerEditPage,
  QRCodeManagerListPage,
  QRCodeManagerViewPage,
  VerifyCodePage,
  CommonDynamicModuleListPage,
  CommonDynamicModuleCreatePage,
  CommonDynamicModuleEditPage,
  CommonDynamicModuleViewPage,

  // Dashboard: User
  // UserListPage,
} from './elements';
import { MODULE_PATHS, PATH_DASHBOARD } from './paths';

// ----------------------------------------------------------------------
const { CREATE, READ, UPDATE } = USER_RIGHTS_KEYS;
// ----------------------------------------------------------------------

export default function Router() {
  const { pathname } = useLocation();
  const defaultHomeSlug = 'app';
  const slug = getSlugByPathname(pathname);

  const dynamicModuleRoutes =
    // exclude staff maangement page
    slug !== PROJECT_MODULES_SLUGS.STAFF_MANAGEMENT &&
    // exclude QR code manager page
    slug !== PROJECT_MODULES_SLUGS.QR_CODE_MANAGER &&
    // exclude home page
    slug !== defaultHomeSlug &&
    // exclude dynamic form customization page
    pathname !== PATH_DASHBOARD.configuration.dynamicForm
      ? {
          path: slug,
          children: [
            {
              element: <Navigate to={`/dashboard/${slug}/${MODULE_PATHS.list}`} replace />,
              index: true,
            },
            {
              path: MODULE_PATHS.list,
              element: (
                <AuthAccessWrapper slug={slug} right={READ} page>
                  <CommonDynamicModuleListPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: MODULE_PATHS.new,
              element: (
                <AuthAccessWrapper slug={slug} right={CREATE} page>
                  <CommonDynamicModuleCreatePage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: `:id/${MODULE_PATHS.edit}`,
              element: (
                <AuthAccessWrapper slug={slug} right={UPDATE} page>
                  <CommonDynamicModuleEditPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: `:id/${MODULE_PATHS.view}`,
              element: (
                <AuthAccessWrapper slug={slug} right={READ} page>
                  <CommonDynamicModuleViewPage />
                </AuthAccessWrapper>
              ),
            },
          ],
        }
      : {};
  return useRoutes([
    // Main Page "/" Route
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [{ element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true }],
    },

    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <LoginPage /> },
        { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: 'reset-password', element: <ResetPasswordPage /> },
            { path: 'new-password', element: <NewPasswordPage /> },
            { path: 'verify', element: <VerifyCodePage /> },
          ],
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: defaultHomeSlug, element: <GeneralAppPage /> },

        //----------------------------------------------------------------------------------------
        dynamicModuleRoutes,
        {
          path: 'staff-management',
          children: [
            {
              element: <Navigate to="/dashboard/staff-management/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.staffManagement.slug} right={READ} page>
                  <StaffManagementListPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: 'new',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.staffManagement.slug} right={CREATE} page>
                  <StaffManagementCreatePage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.staffManagement.slug} right={UPDATE} page>
                  <StaffManagementEditPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: ':id/view',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.staffManagement.slug} right={READ} page>
                  <StaffManagementViewPage />
                </AuthAccessWrapper>
              ),
            },
          ],
        },
        {
          path: 'qr-code-manager',
          children: [
            {
              element: <Navigate to="/dashboard/qr-code-manager/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.qrCodeManager.slug} right={READ} page>
                  <QRCodeManagerListPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: 'new',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.qrCodeManager.slug} right={CREATE} page>
                  <QRCodeManagerCreatePage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.qrCodeManager.slug} right={UPDATE} page>
                  <QRCodeManagerEditPage />
                </AuthAccessWrapper>
              ),
            },
            {
              path: ':id/view',
              element: (
                <AuthAccessWrapper slug={PATH_DASHBOARD.qrCodeManager.slug} right={READ} page>
                  <QRCodeManagerViewPage />
                </AuthAccessWrapper>
              ),
            },
          ],
        },
        {
          path: 'configuration',
          children: [
            {
              element: <Navigate to="/dashboard/configuration/dynamic-form" replace />,
              index: true,
            },
            {
              path: 'dynamic-form',
              element: (
                // <AuthAccessWrapper right={UPDATE} page>
                <DynamicFormEditPage />
                // </AuthAccessWrapper>
              ),
            },
          ],
        },
        // {
        //   path: 'project-management',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/project-management/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectManagement.slug} right={READ} page>
        //           <ProjectManagementListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectManagement.slug} right={CREATE} page>
        //           <ProjectManagementCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectManagement.slug} right={UPDATE} page>
        //           <ProjectManagementEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectManagement.slug} right={READ} page>
        //           <ProjectManagementViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'product-management',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/product-management/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productManagement.slug} right={READ} page>
        //           <ProductManagementListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productManagement.slug} right={CREATE} page>
        //           <ProductManagementCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productManagement.slug} right={UPDATE} page>
        //           <ProductManagementEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productManagement.slug} right={READ} page>
        //           <ProductManagementViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'doctor-management',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/doctor-management/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.doctorManagement.slug} right={READ} page>
        //           <DoctorManagementListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.doctorManagement.slug} right={CREATE} page>
        //           <DoctorManagementCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.doctorManagement.slug} right={UPDATE} page>
        //           <DoctorManagementEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.doctorManagement.slug} right={READ} page>
        //           <DoctorManagementViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },

        // {
        //   path: 'job-listing',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/job-listing/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobListing.slug} right={READ} page>
        //           <JobListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobListing.slug} right={CREATE} page>
        //           <JobCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobListing.slug} right={UPDATE} page>
        //           <JobEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobListing.slug} right={READ} page>
        //           <JobViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },

        // {
        //   path: 'banner',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/banner/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.banner.slug} right={READ} page>
        //           <BannerListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.banner.slug} right={CREATE} page>
        //           <BannerCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.banner.slug} right={UPDATE} page>
        //           <BannerEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.banner.slug} right={READ} page>
        //           <BannerViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },

        // {
        //   path: 'gallery',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/gallery/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.gallery.slug} right={READ} page>
        //           <GalleryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.gallery.slug} right={CREATE} page>
        //           <GalleryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.gallery.slug} right={UPDATE} page>
        //           <GalleryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.gallery.slug} right={READ} page>
        //           <GalleryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'testimonial',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/testimonial/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.testimonial.slug} right={READ} page>
        //           <TestimonialListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.testimonial.slug} right={CREATE} page>
        //           <TestimonialCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.testimonial.slug} right={UPDATE} page>
        //           <TestimonialEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.testimonial.slug} right={READ} page>
        //           <TestimonialViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'association',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/association/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.association.slug} right={READ} page>
        //           <AssociationListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.association.slug} right={CREATE} page>
        //           <AssociationCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.association.slug} right={UPDATE} page>
        //           <AssociationEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.association.slug} right={READ} page>
        //           <AssociationViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'post',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/post/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.post.slug} right={READ} page>
        //           <PostListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.post.slug} right={CREATE} page>
        //           <PostCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.post.slug} right={UPDATE} page>
        //           <PostEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.post.slug} right={READ} page>
        //           <PostViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'company-management',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/company-management/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.companyManagement.slug} right={READ} page>
        //           <CompanyListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.companyManagement.slug} right={CREATE} page>
        //           <CompanyCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.companyManagement.slug} right={UPDATE} page>
        //           <CompanyEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.companyManagement.slug} right={READ} page>
        //           <CompanyViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'qualification-level',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/qualification-level/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.qualificationLevel.slug} right={READ} page>
        //           <QualificationLevelListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper
        //           slug={PATH_DASHBOARD.qualificationLevel.slug}
        //           right={CREATE}
        //           page
        //         >
        //           <QualificationLevelCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper
        //           slug={PATH_DASHBOARD.qualificationLevel.slug}
        //           right={UPDATE}
        //           page
        //         >
        //           <QualificationLevelEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.qualificationLevel.slug} right={READ} page>
        //           <QualificationLevelViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'project-type',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/project-type/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectType.slug} right={READ} page>
        //           <ProjectTypeListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectType.slug} right={CREATE} page>
        //           <ProjectTypeCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectType.slug} right={UPDATE} page>
        //           <ProjectTypeEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.projectType.slug} right={READ} page>
        //           <ProjectTypeViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'country',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/country/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.country.slug} right={READ} page>
        //           <CountryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.country.slug} right={CREATE} page>
        //           <CountryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.country.slug} right={UPDATE} page>
        //           <CountryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.country.slug} right={READ} page>
        //           <CountryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'state',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/state/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.state.slug} right={READ} page>
        //           <StateListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.state.slug} right={CREATE} page>
        //           <StateCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.state.slug} right={UPDATE} page>
        //           <StateEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.state.slug} right={READ} page>
        //           <StateViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'city',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/city/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.city.slug} right={READ} page>
        //           <CityListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.city.slug} right={CREATE} page>
        //           <CityCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.city.slug} right={UPDATE} page>
        //           <CityEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.city.slug} right={READ} page>
        //           <CityViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'industry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/industry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.industry.slug} right={READ} page>
        //           <IndustryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.industry.slug} right={CREATE} page>
        //           <IndustryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.industry.slug} right={UPDATE} page>
        //           <IndustryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.industry.slug} right={READ} page>
        //           <IndustryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'specialization',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/specialization/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.specialization.slug} right={READ} page>
        //           <SpecializationListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.specialization.slug} right={CREATE} page>
        //           <SpecializationCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.specialization.slug} right={UPDATE} page>
        //           <SpecializationEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.specialization.slug} right={READ} page>
        //           <SpecializationViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'location',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/location/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.location.slug} right={READ} page>
        //           <LocationListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.location.slug} right={CREATE} page>
        //           <LocationCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.location.slug} right={UPDATE} page>
        //           <LocationEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.location.slug} right={READ} page>
        //           <LocationViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'category',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/category/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.category.slug} right={READ} page>
        //           <CategoryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.category.slug} right={CREATE} page>
        //           <CategoryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.category.slug} right={UPDATE} page>
        //           <CategoryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.category.slug} right={READ} page>
        //           <CategoryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'sub-category',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/sub-category/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.subCategory.slug} right={READ} page>
        //           <SubCategoryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.subCategory.slug} right={CREATE} page>
        //           <SubCategoryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.subCategory.slug} right={UPDATE} page>
        //           <SubCategoryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.subCategory.slug} right={READ} page>
        //           <SubCategoryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'super-sub-category',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/super-sub-category/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.superSubCategory.slug} right={READ} page>
        //           <SuperSubCategoryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.superSubCategory.slug} right={CREATE} page>
        //           <SuperSubCategoryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.superSubCategory.slug} right={UPDATE} page>
        //           <SuperSubCategoryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.superSubCategory.slug} right={READ} page>
        //           <SuperSubCategoryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'feature-type',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/feature-type/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.featureType.slug} right={READ} page>
        //           <FeatureTypeCreateEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'brands',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/brands/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.brands.slug} right={READ} page>
        //           <BrandListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.brands.slug} right={CREATE} page>
        //           <BrandCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.brands.slug} right={UPDATE} page>
        //           <BrandEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     // {
        //     //   path: ':id/view',
        //     //   element: (
        //     //     <AuthAccessWrapper slug={PATH_DASHBOARD.brands.slug} right={READ} page>
        //     //       <BrandViewPage />
        //     //     </AuthAccessWrapper>
        //     //   ),
        //     // },
        //   ],
        // },
        // {
        //   path: 'job-application',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/job-application/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobApplication.slug} right={READ} page>
        //           <JobApplicationListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.jobApplication.slug} right={READ} page>
        //           <JobApplicationViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'contact-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/contact-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.contactEnquiry.slug} right={READ} page>
        //           <ContactEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.contactEnquiry.slug} right={READ} page>
        //           <ContactEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'appointment-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/appointment-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.appointmentEnquiry.slug} right={READ} page>
        //           <AppointmentEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: 'new',
        //       element: (
        //         <AuthAccessWrapper
        //           slug={PATH_DASHBOARD.appointmentEnquiry.slug}
        //           right={CREATE}
        //           page
        //         >
        //           <AppointmentEnquiryCreatePage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/edit',
        //       element: (
        //         <AuthAccessWrapper
        //           slug={PATH_DASHBOARD.appointmentEnquiry.slug}
        //           right={UPDATE}
        //           page
        //         >
        //           <AppointmentEnquiryEditPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.appointmentEnquiry.slug} right={READ} page>
        //           <AppointmentEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'career-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/career-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.careerEnquiry.slug} right={READ} page>
        //           <CareerEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.careerEnquiry.slug} right={READ} page>
        //           <CareerEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'dealership-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/dealership-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.dealershipEnquiry.slug} right={READ} page>
        //           <DealershipEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.dealershipEnquiry.slug} right={READ} page>
        //           <DealershipEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'service-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/service-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.serviceEnquiry.slug} right={READ} page>
        //           <ServiceEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.serviceEnquiry.slug} right={READ} page>
        //           <ServiceEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'quotation-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/quotation-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.quotationEnquiry.slug} right={READ} page>
        //           <QuotationEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.quotationEnquiry.slug} right={READ} page>
        //           <QuotationEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'product-enquiry',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/product-enquiry/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productEnquiry.slug} right={READ} page>
        //           <ProductEnquiryListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':id/view',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.productEnquiry.slug} right={READ} page>
        //           <ProductEnquiryViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'feedback',
        //   children: [
        //     {
        //       element: <Navigate to="/dashboard/feedback/list" replace />,
        //       index: true,
        //     },
        //     {
        //       path: 'list',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.feedback.slug} right={READ} page>
        //           <FeedbackListPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //     {
        //       path: ':enquiryId/view/:documentId',
        //       element: (
        //         <AuthAccessWrapper slug={PATH_DASHBOARD.feedback.slug} right={READ} page>
        //           <FeedbackViewPage />
        //         </AuthAccessWrapper>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'user',
        //   children: [
        //     { element: <Navigate to="/dashboard/user/list" replace />, index: true },
        //     { path: 'list', element: <UserListPage /> },
        //     // { path: 'profile', element: <UserProfilePage /> },
        //     // { path: 'cards', element: <UserCardsPage /> },
        //     // { path: 'new', element: <UserCreatePage /> },
        //     // { path: ':name/edit', element: <UserEditPage /> },
        //     // { path: 'account', element: <UserAccountPage /> },
        //   ],
        // },
      ],
    },

    // Main Routes
    {
      element: <MainLayout />,
      children: [{ element: <HomePage />, index: true }],
    },

    {
      element: <CompactLayout />,
      children: [{ path: '500', element: <Page500 /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
